import { useEffect } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import apiService from '../services/ApiService';
import APIClient from '../services/APIClient';
import WeaponeeringAPI from '../services/WeaponeeringAPIClient';
import AdversaryAPI from '../services/adversary/AdversaryAPIClient';
import AiPlatformAPI from '../services/aiPlatform/AiPlatformAPIClient';

// Token-renewal failures that mean the SESSION is unrecoverable without an
// interactive login: no refresh token in the cache (pre-fix logins, cleared
// storage), an expired/revoked refresh token, or an expired Auth0 SSO session.
// Anything else (network blips, timeouts) is transient and must NOT log the
// user out.
const SESSION_EXPIRED_CODES = new Set(['missing_refresh_token', 'login_required', 'invalid_grant', 'consent_required']);
const isSessionExpired = (err) =>
  SESSION_EXPIRED_CODES.has(err?.error) || /missing refresh token/i.test(err?.message || '');

// Module-level so parallel API calls trigger exactly one redirect. Reset on a
// timer in case the user cancels the Universal Login page and comes back.
let reloginInFlight = false;

/**
 * Hook to provide authenticated API service
 * Automatically manages Auth0 access tokens for API requests
 * Syncs tokens to both ApiService and APIClient
 */
export function useApi() {
  const { getAccessTokenSilently, loginWithRedirect, isAuthenticated } = useAuth0();

  useEffect(() => {
    if (isAuthenticated) {
      // Register an on-demand token provider so every APIClient request resolves a
      // valid token right before it fires (the Auth0 SDK returns the cached token
      // when still valid and only refreshes when needed). This removes the window
      // where a transiently-cleared cached token caused protected calls (COA save,
      // agent endpoints) to 403 "Not authenticated".
      //
      // When renewal fails because the SESSION is dead (missing/expired refresh
      // token), bounce straight to a clean sign-in that returns to the current
      // page — one visible redirect instead of an app full of silent 403s.
      const provider = async () => {
        try {
          return await getAccessTokenSilently();
        } catch (err) {
          if (isSessionExpired(err) && !reloginInFlight) {
            reloginInFlight = true;
            setTimeout(() => { reloginInFlight = false; }, 15000);
            console.warn(`Session expired (${err?.error || 'missing refresh token'}) — redirecting to sign-in.`);
            loginWithRedirect({
              appState: { returnTo: window.location.pathname + window.location.search },
            });
          }
          throw err;
        }
      };
      APIClient.setTokenProvider(provider);
      apiService.setTokenProvider(provider);
      // AMD / weaponeering calls go through the WeaponeeringAPIClient singleton;
      // wire it the same way so protected /amd endpoints receive the token.
      WeaponeeringAPI.setTokenProvider(provider);
      // Adversary Deployment Planning calls (/adversary endpoints) likewise.
      AdversaryAPI.setTokenProvider(provider);
      // Platform Management's AI Models tab (/platform/ai endpoints).
      AiPlatformAPI.setTokenProvider(provider);

      // Prime the cached token once so the very first calls have it immediately.
      const primeToken = async () => {
        try {
          const token = await provider();
          apiService.setAuthToken(token);
          APIClient.setAuthToken(token);
          WeaponeeringAPI.setAuthToken(token);
          AdversaryAPI.setAuthToken(token);
          AiPlatformAPI.setAuthToken(token);
        } catch (error) {
          // A transient failure here no longer strands protected calls — the
          // provider will retry on demand — so keep any previously-good token.
          console.warn('Initial access-token fetch failed (will retry on demand):', error?.message || error);
        }
      };
      primeToken();
    } else {
      apiService.clearAuthToken();
      apiService.clearTokenProvider();
      APIClient.clearAuthToken();
      APIClient.clearTokenProvider();
      WeaponeeringAPI.clearAuthToken();
      WeaponeeringAPI.clearTokenProvider();
      AdversaryAPI.clearAuthToken();
      AdversaryAPI.clearTokenProvider();
      AiPlatformAPI.clearAuthToken();
      AiPlatformAPI.clearTokenProvider();
    }
  }, [isAuthenticated, getAccessTokenSilently, loginWithRedirect]);

  return apiService;
}
